import { useState, useRef, useEffect, useCallback } from 'react';
import { usePerson, usePersons } from '@/hooks/usePersons';
import type { Person } from '@/api/persons.api';

// ─── Props ─────────────────────────────────────────────────────────────────

interface PersonAutocompleteProps {
  /** Aktuelle Person-ID (gebunden an das Formularfeld) */
  value: string | null;
  /** Callback wenn eine Person ausgewählt wird */
  onChange: (personId: string | null) => void;
  /** Optional: liefert die vollständige Person bei Auswahl (z. B. für Multi-Select). */
  onSelectPerson?: (person: Person) => void;
  /** Placeholder-Text */
  placeholder?: string;
  /** Personen-IDs die aus der Auswahlliste ausgeschlossen werden */
  excludeIds?: string[];
  /** Deaktiviert das Eingabefeld */
  disabled?: boolean;
  /** Label für Barrierefreiheit */
  'aria-label'?: string;
}

// ─── PersonAutocomplete ────────────────────────────────────────────────────

export function PersonAutocomplete({
  value,
  onChange,
  onSelectPerson,
  placeholder = 'Person suchen...',
  excludeIds = [],
  disabled = false,
  'aria-label': ariaLabel,
}: PersonAutocompleteProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedName, setSelectedName] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ─── Debounce (300ms) ──────────────────────────────────────────────────

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // ─── Personensuche via TanStack Query ─────────────────────────────────
  // Limit um die ausgeschlossenen IDs erhöhen, sonst kann der Client-Filter
  // alle Server-Treffer wegfiltern und fälschlich "Keine Personen gefunden"
  // anzeigen, obwohl weitere Treffer existieren.

  const { data: results, isLoading } = usePersons(
    debouncedQuery.length >= 2
      ? { q: debouncedQuery, limit: Math.min(50, 10 + excludeIds.length) }
      : undefined,
  );

  const filteredPersons =
    results?.data?.filter((p) => !excludeIds.includes(p.id)) ?? [];

  // ─── Klick außerhalb schließt Dropdown ────────────────────────────────

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── Wenn value von außen gesetzt wird, Namen laden ───────────────────
  // Gezielter Einzel-Fetch statt Personenliste: Die Liste ist serverseitig
  // auf 100 Einträge begrenzt, sodass die Namensauflösung in größeren
  // Stammbäumen sonst fehlschlägt.

  const { data: selectedPerson } = usePerson(value && !selectedName ? value : null);

  useEffect(() => {
    if (!value) {
      setSelectedName('');
      return;
    }
    if (selectedPerson && selectedPerson.id === value) {
      setSelectedName(formatName(selectedPerson));
    }
  }, [value, selectedPerson]);

  // ─── Auswahl einer Person ─────────────────────────────────────────────

  const handleSelect = useCallback(
    (person: Person) => {
      onChange(person.id);
      onSelectPerson?.(person);
      setSelectedName(formatName(person));
      setQuery('');
      setIsOpen(false);
    },
    [onChange, onSelectPerson],
  );

  // ─── Auswahl löschen ─────────────────────────────────────────────────

  const handleClear = useCallback(() => {
    onChange(null);
    setSelectedName('');
    setQuery('');
    inputRef.current?.focus();
  }, [onChange]);

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div ref={containerRef} className="relative">
      {/* Ausgewählte Person (Chip) */}
      {value && selectedName ? (
        <div className="flex items-center gap-1.5 rounded border border-slate-300 bg-white px-2.5 py-1.5">
          <span className="flex-1 truncate text-sm text-slate-900">{selectedName}</span>
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              aria-label="Auswahl entfernen"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      ) : (
        /* Suchfeld */
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (query.length >= 2) setIsOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              // Enter darf nicht das umgebende Formular absenden;
              // bei eindeutigem Treffer wird dieser übernommen.
              e.preventDefault();
              if (isOpen && filteredPersons.length === 1) {
                handleSelect(filteredPersons[0]);
              }
            } else if (e.key === 'Escape' && isOpen) {
              // Nur das Dropdown schließen, nicht das Modal dahinter
              e.stopPropagation();
              setIsOpen(false);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={ariaLabel}
          className="w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900
                     placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400
                     disabled:bg-slate-50 disabled:text-slate-400"
          autoComplete="off"
        />
      )}

      {/* Dropdown-Liste */}
      {isOpen && !value && debouncedQuery.length >= 2 && (
        <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {isLoading && (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-slate-400">
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
              Suche...
            </div>
          )}

          {!isLoading && filteredPersons.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-400">
              Keine Personen gefunden.
            </div>
          )}

          {filteredPersons.map((person) => (
            <button
              key={person.id}
              type="button"
              onClick={() => handleSelect(person)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-blue-50"
            >
              {/* Gender-Icon */}
              <span className="text-xs text-slate-400">
                {person.gender === 'male' ? '♂' : person.gender === 'female' ? '♀' : '◎'}
              </span>
              <div className="min-w-0 flex-1">
                <span className="font-medium text-slate-800">
                  {person.firstName} {person.lastName ?? ''}
                </span>
                {(person.birthDateText || person.deathDateText) && (
                  <span className="ml-1.5 text-xs text-slate-400">
                    ({person.birthDateText ?? '?'} – {person.deathDateText ?? (person.isLiving ? 'lebend' : '?')})
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Hilfsfunktion ─────────────────────────────────────────────────────────

function formatName(person: Person): string {
  return `${person.firstName} ${person.lastName ?? ''}`.trim();
}
